import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Sparkles, ArrowLeft, Mail } from "lucide-react";
import { api } from "../lib/api";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0F0E13] text-[#F7F5F2] flex items-center justify-center p-8">
      <div className="w-full max-w-md fade-up">
        <Link to="/" className="flex items-center gap-2 mb-12" data-testid="forgot-logo-link">
          <Sparkles className="w-4 h-4 text-[#E3C2B0]" strokeWidth={1.5} />
          <span className="font-serif text-2xl">StyleMe</span>
        </Link>

        {sent ? (
          <div data-testid="forgot-sent">
            <div className="w-12 h-12 rounded-full bg-[#E3C2B0]/15 flex items-center justify-center mb-8">
              <Mail className="w-5 h-5 text-[#E3C2B0]" strokeWidth={1.5} />
            </div>
            <h1 className="font-serif text-4xl font-light leading-tight">Check your inbox.</h1>
            <p className="mt-6 text-[#A8A6A1] leading-relaxed">
              If an account exists for <span className="text-[#F7F5F2]">{email}</span>, we've sent a reset link.
              The link is valid for 60 minutes.
            </p>
            <p className="mt-4 text-xs uppercase tracking-[0.3em] text-[#A8A6A1]">
              Don't see it? Check spam, or try again in a minute.
            </p>
            <Link
              to="/auth?mode=login"
              data-testid="forgot-back-to-login"
              className="mt-12 inline-flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-[#E3C2B0] hover:text-[#F0D4C5]"
            >
              <ArrowLeft className="w-3 h-3" strokeWidth={1.5} /> Back to sign in
            </Link>
          </div>
        ) : (
          <>
            <span className="text-xs uppercase tracking-[0.4em] text-[#E3C2B0]">Forgot password</span>
            <h1 className="font-serif text-5xl mt-4 font-light leading-tight">
              Let's reset it.
            </h1>
            <p className="mt-4 text-[#A8A6A1] leading-relaxed">
              Enter the email tied to your account. We'll send you a secure link to choose a new password.
            </p>

            <form onSubmit={submit} className="mt-10 space-y-8">
              <div>
                <label className="text-xs uppercase tracking-[0.3em] text-[#A8A6A1] block mb-3">Email</label>
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="forgot-email"
                  className="w-full bg-transparent border-b border-white/15 focus:border-[#E3C2B0] outline-none py-3 text-[#F7F5F2] transition-colors text-lg font-light"
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                data-testid="forgot-submit"
                className="w-full bg-[#E3C2B0] text-[#0A0908] py-4 rounded-sm hover:bg-[#F0D4C5] transition-colors text-xs uppercase tracking-[0.3em] disabled:opacity-50"
              >
                {loading ? "Sending..." : "Send reset link"}
              </button>
            </form>

            <Link
              to="/auth?mode=login"
              data-testid="forgot-back-link"
              className="mt-8 inline-flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-[#A8A6A1] hover:text-[#F7F5F2]"
            >
              <ArrowLeft className="w-3 h-3" strokeWidth={1.5} /> Back to sign in
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
